import React from 'react';

interface SkeletonProps {
  variant?: 'text' | 'rectangular' | 'circular' | 'card' | 'product';
  width?: string | number;
  height?: string | number;
  className?: string;
  count?: number;
  animation?: 'pulse' | 'wave' | 'none';
}

const Skeleton: React.FC<SkeletonProps> = ({
  variant = 'rectangular',
  width,
  height,
  className = '',
  count = 1,
  animation = 'pulse',
}) => {
  const getVariantClasses = (): string => {
    switch (variant) {
      case 'text':
        return 'h-4 rounded';
      case 'circular':
        return 'rounded-full';
      case 'rectangular':
        return 'rounded';
      case 'card':
        return 'rounded-lg';
      case 'product':
        return 'rounded-lg aspect-square';
      default:
        return 'rounded';
    }
  };

  const getAnimationClasses = (): string => {
    switch (animation) {
      case 'pulse':
        return 'animate-pulse';
      case 'wave':
        return 'animate-shimmer bg-gradient-to-r from-canvas-primary via-canvas-secondary to-canvas-primary bg-[length:200%_100%]';
      case 'none':
        return '';
      default:
        return 'animate-pulse';
    }
  };

  const baseClasses = 'bg-canvas-secondary';
  const variantClasses = getVariantClasses();
  const animationClasses = getAnimationClasses();

  const style: React.CSSProperties = {
    width: width || (variant === 'text' ? '100%' : undefined),
    height: height || (variant === 'text' ? '1rem' : variant === 'circular' ? width : undefined),
  };

  const skeletonElement = (
    <div
      className={`${baseClasses} ${variantClasses} ${animationClasses} ${className}`}
      style={style}
    />
  );

  if (count > 1) {
    return (
      <div className="space-y-2">
        {Array.from({ length: count }).map((_, index) => (
          <div key={index}>{skeletonElement}</div>
        ))}
      </div>
    );
  }

  return skeletonElement;
};

// Specialized skeleton components for common use cases
export const SkeletonCard: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`bg-canvas-secondary border border-stroke-primary rounded-lg p-4 ${className}`}>
    <Skeleton variant="product" className="mb-4" />
    <Skeleton variant="text" width="80%" className="mb-2" />
    <Skeleton variant="text" width="60%" className="mb-4" />
    <Skeleton variant="rectangular" height="2.5rem" />
  </div>
);

export const SkeletonTable: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 5,
  columns = 4,
}) => (
  <div className="bg-canvas-secondary border border-stroke-primary rounded-lg overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-canvas-primary border-b border-stroke-primary">
          <tr>
            {Array.from({ length: columns }).map((_, index) => (
              <th key={index} className="px-6 py-4">
                <Skeleton variant="text" height="1rem" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-stroke-primary">
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <tr key={rowIndex} className="hover:bg-canvas-primary transition-colors">
              {Array.from({ length: columns }).map((_, colIndex) => (
                <td key={colIndex} className="px-6 py-4">
                  <Skeleton variant="text" height="1rem" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export const SkeletonProductCard: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`group bg-canvas-secondary rounded-lg overflow-hidden border border-stroke-primary ${className}`}>
    <div className="aspect-square overflow-hidden">
      <Skeleton variant="rectangular" className="w-full h-full" />
    </div>
    <div className="p-4">
      <Skeleton variant="text" width="40%" className="mb-2" height="0.75rem" />
      <Skeleton variant="text" width="90%" className="mb-2" height="1.25rem" />
      <Skeleton variant="text" width="60%" className="mb-3" height="0.875rem" />
      <div className="flex items-center justify-between mb-3">
        <Skeleton variant="text" width="30%" height="1.5rem" />
        <Skeleton variant="text" width="25%" height="0.875rem" />
      </div>
      <Skeleton variant="rectangular" height="2.5rem" className="rounded-lg" />
    </div>
  </div>
);

export const SkeletonAvatar: React.FC<{ size?: number; className?: string }> = ({
  size = 40,
  className = '',
}) => <Skeleton variant="circular" width={size} height={size} className={className} />;

export const SkeletonText: React.FC<{
  lines?: number;
  className?: string;
}> = ({ lines = 3, className = '' }) => (
  <div className={`space-y-2 ${className}`}>
    {Array.from({ length: lines }).map((_, index) => (
      <Skeleton
        key={index}
        variant="text"
        width={index === lines - 1 ? '70%' : '100%'}
        height="1rem"
      />
    ))}
  </div>
);

export default Skeleton;
